import { useEffect, useState } from "react";
import { MenuItem, MenuList } from "@mui/material";
import DatasetAnalyticsExportMenu from "./DatasetAnalyticsExportMenu";
import {
  calculateAnalyticResultsUrl,
  calculateRawPredictionsUrl,
  useExportCSV,
} from "../services/DatasetServices";
import PropTypes from "prop-types";
import { LoadingButton } from "@mui/lab";
import { PowerBIDataModal } from "src/reusable/components/PowerBIComponents";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import DataObject from "@mui/icons-material/DataObject";
import TableView from "@mui/icons-material/TableView";

const DatasetAnalyticsExportButton = ({
  analyticId,
  isDisabled,
  onReadyToRunChange,
  onStatsPaneChange,
  setIsRawPredictionsModalVisible,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isExportRequested, setIsExportRequested] = useState(false);
  const [isPowerBIModalOpen, setIsPowerBIModalOpen] = useState(false);
  const [currentPowerBIActionMetadata, setCurrentPowerBIActionMetadata] =
    useState({
      url: "",
      message: "",
    });
  const [exportType, setExportType] = useState("csv");

  const open = Boolean(anchorEl);

  const { mutate: exportToCSV, isLoading: exportToCSVLoading } = useExportCSV();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExportCsv = () => {
    if (analyticId) {
      handleClose();
      exportToCSV({ analyticId, onReadyToRunChange });
    } else {
      setExportType("csv");
      setIsExportRequested(true);
      onReadyToRunChange(true);
    }
  };

  const handleExportRawPredictionsCsv = () => {
    handleClose();
    onReadyToRunChange(true);
    setIsRawPredictionsModalVisible(true);
  };

  const handleExportJson = () => {
    handleClose();
    onStatsPaneChange("raw");
    onReadyToRunChange(true);
  };

  const handleExportTable = () => {
    handleClose();
    onStatsPaneChange("view");
    onReadyToRunChange(true);
  };

  const handleExportAnalysisPowerBI = () => {
    if (analyticId) {
      handleClose();
      setCurrentPowerBIActionMetadata({
        url: calculateAnalyticResultsUrl(analyticId),
        message: " to this analysis",
      });
      setIsPowerBIModalOpen(true);
    } else {
      setExportType("powerbi-analysis");
      setIsExportRequested(true);
      onReadyToRunChange(true);
    }
  };

  const handleExportRawPredictionsPowerBI = () => {
    if (analyticId) {
      handleClose();
      setCurrentPowerBIActionMetadata({
        url: calculateRawPredictionsUrl(analyticId),
        message: " to raw predictions",
      });
      setIsPowerBIModalOpen(true);
    } else {
      setExportType("powerbi-raw-predictions");
      setIsExportRequested(true);
      onReadyToRunChange(true);
    }
  };

  useEffect(() => {
    if (analyticId && isExportRequested) {
      handleClose();
      // handles the export request
      switch (exportType) {
        case "powerbi-analysis":
          setCurrentPowerBIActionMetadata((prev) => ({
            ...prev,
            url: calculateAnalyticResultsUrl(analyticId),
            message: " to this analysis",
          }));
          setIsPowerBIModalOpen(true);
          break;
        case "powerbi-raw-predictions":
          setCurrentPowerBIActionMetadata((prev) => ({
            ...prev,
            url: calculateRawPredictionsUrl(analyticId),
            message: " to raw predictions",
          }));
          setIsPowerBIModalOpen(true);
          break;
        case "csv":
          exportToCSV({ analyticId, onReadyToRunChange });
          break;
        default:
          break; // Add default case here
      }
      setIsExportRequested(false);
    }
  }, [analyticId, isExportRequested]);

  return (
    <>
      <PowerBIDataModal
        isVisible={isPowerBIModalOpen}
        onClose={() => setIsPowerBIModalOpen(false)}
        message={currentPowerBIActionMetadata?.message}
        url={currentPowerBIActionMetadata?.url}
      />
      <div>
        <LoadingButton
          id="dataset-analytics-export-button"
          data-attr="datasets:btn:export-analytics"
          aria-controls={open ? "dataset-analytics-export-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          color="gray"
          variant="contained"
          disableElevation
          onClick={handleClick}
          loading={exportToCSVLoading}
          loadingPosition="end"
          endIcon={<KeyboardArrowDown />}
          disabled={isDisabled}
        >
          {exportToCSVLoading ? "Exporting" : "Export"}
        </LoadingButton>
        <DatasetAnalyticsExportMenu
          id="dataset-analytics-export-menu"
          MenuListProps={{
            "aria-labelledby": "dataset-analytics-export-button",
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <MenuList>
            <MenuItem
              onClick={handleExportCsv}
              disableRipple
              data-attr="datasets:menu:export-analysis-csv"
            >
              <>
                <img
                  src={"/images/favicon/excel_icon.png"}
                  style={{ width: "18px", marginRight: "14px" }}
                />
              </>
              Analysis Data - CSV
            </MenuItem>
            <MenuItem
              onClick={handleExportRawPredictionsCsv}
              disableRipple
              data-attr="datasets:menu:export-raw-predictions-csv"
            >
              <>
                <img
                  src={"/images/favicon/excel_icon.png"}
                  style={{ width: "18px", marginRight: "14px" }}
                />
              </>
              Raw Predictions - CSV
            </MenuItem>
            <MenuItem divider />
            <MenuItem
              onClick={handleExportJson}
              disableRipple
              data-attr="datasets:menu:export-analysis-json"
            >
              <DataObject />
              Analysis Data - JSON
            </MenuItem>

            <MenuItem
              onClick={handleExportTable}
              disableRipple
              data-attr="datasets:menu:export-analysis-table"
            >
              <TableView />
              Analysis Data - Table
            </MenuItem>

            <MenuItem divider />
            <MenuItem
              onClick={handleExportAnalysisPowerBI}
              disableRipple
              data-attr="datasets:menu:export-analysis-powerbi"
            >
              <img
                src={"/svgs/Power-BI.svg"}
                style={{ width: "16px", marginRight: "14px" }}
              />
              Analysis Data - Power BI
            </MenuItem>
            <MenuItem
              onClick={handleExportRawPredictionsPowerBI}
              disableRipple
              data-attr="datasets:menu:export-raw-predictions-powerbi"
            >
              <img
                src={"/svgs/Power-BI.svg"}
                style={{ width: "16px", marginRight: "14px" }}
              />
              Raw Predictions - Power BI
            </MenuItem>
          </MenuList>
        </DatasetAnalyticsExportMenu>
      </div>
    </>
  );
};

DatasetAnalyticsExportButton.propTypes = {
  analyticId: PropTypes.string,
  isDisabled: PropTypes.bool,
  onReadyToRunChange: PropTypes.func,
  onStatsPaneChange: PropTypes.func,
  setIsRawPredictionsModalVisible: PropTypes.func,
};

export default DatasetAnalyticsExportButton;
